import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanLine, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import type { Paged } from './pagedList';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Badge, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

interface StudyType {
  id: string;
  code: string;
  name: string;
  modality: string;
  bodyPart: string | null;
}

export function RadiologyOrderPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/radiology');
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [priority, setPriority] = useState<'routine' | 'urgent' | 'stat'>('routine');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [pregnancyChecked, setPregnancyChecked] = useState(false);
  const [studyQuery, setStudyQuery] = useState('');
  const [chosen, setChosen] = useState<StudyType[]>([]);
  const [singleInvoice, setSingleInvoice] = useState(false);

  const results = useQuery({
    queryKey: ['radiology-study-types-search', studyQuery],
    queryFn: async () => (await get<Paged<StudyType>>(`/radiology/study-types${qs({ q: studyQuery || undefined, limit: 50 })}`)).data,
    enabled: studyQuery.trim().length > 0,
  });

  const save = useMutation({
    mutationFn: () =>
      post('/radiology/orders', {
        patientId,
        priority,
        clinicalNotes: clinicalNotes || undefined,
        pregnancyChecked,
        studyTypeIds: chosen.map((s) => s.id),
        ...(singleInvoice && chosen.length > 1 ? { singleInvoice: true } : {}),
      }),
    onSuccess: () => {
      toast.success(tr('radiology', 'study_ordered'), tr('radiology', 'x_study_ies_added_to_the_worklist', { length: chosen.length }));
      void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error(tr('radiology', 'could_not_place_the_order'), describeError(error)),
  });

  const ready = Boolean(patientId) && chosen.length > 0 && pregnancyChecked;
  const dirty = !save.isSuccess && (Boolean(patientId) || chosen.length > 0 || clinicalNotes !== '' || pregnancyChecked);

  return (
    <FormScreen
      title={tr('radiology', 'order_imaging')}
      eyebrow={tr('radiology', 'radiology')}
      icon={ScanLine}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title={tr('radiology', 'patient')} icon={UserRound}>
        <Field label={tr('radiology', 'patient')} required className="sm:col-span-2">
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        <Field label={tr('radiology', 'priority')} htmlFor="rad-order-priority">
          <ChoiceButtons<typeof priority>
            id="rad-order-priority"
            value={priority}
            onChange={setPriority}
            inline
            options={(['routine', 'urgent', 'stat'] as const).map((p) => ({ value: p, label: humanize(p) }))}
          />
        </Field>
        <Field label={tr('radiology', 'clinical_notes')} htmlFor="rad-order-notes" className="sm:col-span-2">
          <Textarea id="rad-order-notes" rows={2} value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('radiology', 'study')} icon={ScanLine} columns={1}>
        <Field label={tr('radiology', 'study')} htmlFor="rad-order-study" required hint={tr('radiology', 'search_by_name_code_or_modality')}>
          <Input id="rad-order-study" value={studyQuery} onChange={(e) => setStudyQuery(e.target.value)} placeholder={tr('radiology', 'e_g_chest_x_ray_ct_head')} />
          {results.data && results.data.length > 0 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border">
              {results.data
                .filter((s) => !chosen.some((c) => c.id === s.id))
                .map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-start text-sm hover:bg-accent/50"
                    onClick={() => {
                      setChosen([...chosen, s]);
                      setStudyQuery('');
                    }}
                  >
                    <span>
                      {s.name} <span className="text-xs text-muted-foreground">({s.code})</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{humanize(s.modality, 'radiology_modality')}</span>
                  </button>
                ))}
            </div>
          )}
        </Field>

        {chosen.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((s) => (
              <Badge key={s.id} variant="secondary" className="gap-1">
                {s.name}
                <button
                  type="button"
                  aria-label={tr('radiology', 'remove_x', { name: s.name })}
                  onClick={() => setChosen(chosen.filter((c) => c.id !== s.id))}
                  className="ms-0.5 opacity-70 hover:opacity-100"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        )}

        {chosen.length > 1 && (
          <Checkbox
            checked={singleInvoice}
            onCheckedChange={setSingleInvoice}
            label={tr('radiology', 'bill_these_studies_on_one_invoice')}
            description={tr('radiology', 'off_each_study_gets_its_own_invoice')}
          />
        )}

        <Checkbox
          checked={pregnancyChecked}
          onCheckedChange={setPregnancyChecked}
          label={tr('radiology', 'pregnancy_status_has_been_checked')}
          description={tr('radiology', 'required_before_any_study_involving_ionising')}
        />
      </FormSection>

      <FormActions
        submitLabel={`${tr('radiology', 'place_order')} ${chosen.length} ${tr('radiology', 'study_2')}${chosen.length === 1 ? '' : 'ies'}`}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
